import Decimal from 'decimal.js';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { EntityType } from '@/modules/entities/types/EntityType';
import {
  EntityGstStatus,
  EntityInEstateStatus,
  EntityStage,
  NonTrustEntityTaxStatus,
  TrustTaxStatus,
} from '@/types/schema';

import { ContextPrimaryClient } from '../../household/contexts/householdDetails.context';
import { GetDispositiveProvisions_EstateWaterfallFragment } from '../DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import {
  DispositiveProvisions_BeneficiaryDesignationsFragment,
  DispositiveProvisions_DispositionScenarioFragment,
} from '../graphql/DispositiveProvisions.fragments.generated';

export type DispositiveProvisionsFormVariant =
  | 'manageDispositions'
  | 'editEntity'
  | 'editTestamentaryEntity';

export type DispositiveProvisionsCalculateValues = [
  boolean,
  Dispatch<SetStateAction<boolean>>,
];
export interface DispositiveProvisionsContext {
  id: string;
  primaryClients: ContextPrimaryClient[];
  /**
   * The first grantor dying in a waterfall scenario.
   *
   * `afterDeath` only applies in waterfall views.  If present, it will trigger a different list display.
   * */
  firstGrantorDeathId: string | null;
  dispositionScenarios?: DispositiveProvisions_DispositionScenarioFragment[];
  /**
   * Live-scope BDG for retirement / DH-insurance personal accounts. Consumers
   * must gate on `isBeneficiaryDesignationEntity` before rendering.
   */
  beneficiaryDesignations?: DispositiveProvisions_BeneficiaryDesignationsFragment | null;
  totalMarketValue: Decimal;
  householdId: string;
  isTwoClientHousehold: boolean;
  estateStatus: EntityInEstateStatus | null;
  taxStatus: TrustTaxStatus | NonTrustEntityTaxStatus | null;
  gstStatus: EntityGstStatus | null;
  entityDisplayName: string;
  /** Entity type. This is only relevant if the DP context is for an entity. */
  entityType: EntityType | null;
  /** Entity subtype ID. Relevant for entity targets */
  entitySubtypeId: string | null;
  /** Entity stage. Relevant for entity targets to determine if pre-created entities need activation. */
  entityStage: EntityStage | null;
  isTestamentaryEntity: boolean;
  // Used to determine whether to show the initial empty state in the dispositive provisions form
  hasTestamentaryEntityBeenReviewed: boolean;
  isClientProfile: boolean;
  clientProfileOrEntityOrTestamentaryEntityId: string;
  allClientIds: string[];
  waterfall?: GetDispositiveProvisions_EstateWaterfallFragment;
  isOnHypotheticalWaterfall: boolean;
  formVariant: DispositiveProvisionsFormVariant;
  showCalculatedDispositions: boolean;
  setShowCalculatedDispositions: Dispatch<SetStateAction<boolean>>;
  /**
   * In cases where a user is on the dispositive provision template modal, then opens the dispositive provision
   * form, do not allow the user to then open a second dispositive provision template modal.
   */
  isFromTemplateEditor?: boolean;
  /**
   * Indicates we are editing the root target (true) vs a nested/non-root target (false)
   */
  isEditingRootTarget: boolean;
  /**
   * When the user drills down into a nested (non-root) target, the *current* target remains on the
   * top-level fields.  The original/root target is preserved here so that components that still need
   * household-level or waterfall information for the root can access it.
   */
  rootTargetContext?: RootTargetContext;
}

/**
 * Snapshot of the *root* DP payload that we keep around while editing a nested target.
 * It mirrors the full `DispositiveProvisionsContext`, minus the recursive `rootTargetContext` field
 * (to avoid infinite nesting) and the `isEditingRootTarget` flag (which is always `true` for the
 * root snapshot).
 */
export type RootTargetContext = Omit<
  DispositiveProvisionsContext,
  'rootTargetContext' | 'isEditingRootTarget' | 'formVariant'
>;

export const DispositiveProvisionsContext =
  createContext<DispositiveProvisionsContext>(
    {} as DispositiveProvisionsContext
  );

export const useDispositiveProvisionsContext = () =>
  useGuardedContext(
    DispositiveProvisionsContext,
    'DispositiveProvisionsContext'
  );

export const useUnguardedDispositiveProvisionsContext = () =>
  useContext(DispositiveProvisionsContext);
